import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import FlyerGallery from "./FlyerGallery";
import FlyerGalleryDetailDialog from "./FlyerGalleryDetailDialog";
import FlyerReportCharts from "./FlyerReportCharts";
import FlyerReportFilters from "./FlyerReportFilters";
import FlyerReportSummaryCards from "./FlyerReportSummaryCards";
import { insightsSectionClassName } from "../../common/utils/insightsDesignEnabled";

/**
 * Panel body after access/env gates: filters → alerts → summary → charts accordion → gallery.
 */
export default function FlyerReportPanelContent({ panelState }) {
  const {
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    selectedProvince,
    setSelectedProvince,
    selectedRetailer,
    setSelectedRetailer,
    selectedProduct,
    setSelectedProduct,
    provinceChoices,
    retailerChoices,
    flyerProductFilterChoices,
    flyerReportQueriesReady,
    anyFlyerReportFetchError,
    isFilterOptionsLoading,
    isProductFilterChoicesLoading,
    flyerSummaryStats,
    isSummaryStatsLoading,
    retailerStackedBarChart,
    promotionalFrequencyLineChart,
    provinceOfferCountChart,
    isRetailerChartLoading,
    isFrequencyChartLoading,
    isProvinceChartLoading,
    retailerChartError,
    frequencyChartError,
    provinceChartError,
    galleryDisplayItems,
    galleryFetchError,
    isGalleryInitialLoading,
    isGalleryRefreshing,
    galleryServerPagination,
    selectedGalleryDisplayItem,
    handleGalleryCardClick,
    handleCloseFlyerGalleryDetail,
  } = panelState;

  return (
    <Stack spacing={0} sx={{ py: 0 }}>
      <Box className={insightsSectionClassName("insights-section--filter")}>
        <FlyerReportFilters
          dateFrom={dateFrom}
          dateTo={dateTo}
          setDateFrom={setDateFrom}
          setDateTo={setDateTo}
          provinceChoices={provinceChoices}
          selectedProvince={selectedProvince}
          setSelectedProvince={setSelectedProvince}
          flyerProductFilterChoices={flyerProductFilterChoices}
          selectedProduct={selectedProduct}
          setSelectedProduct={setSelectedProduct}
          retailerChoices={retailerChoices}
          selectedRetailer={selectedRetailer}
          setSelectedRetailer={setSelectedRetailer}
          isLoadingFilterOptions={isFilterOptionsLoading}
          isProductFilterChoicesLoading={isProductFilterChoicesLoading}
        />
      </Box>

      <Box className={insightsSectionClassName("insights-section--content")}>
        <Stack spacing={2} sx={{ mt: 2 }}>
          {!flyerReportQueriesReady ? (
            <Alert severity="warning">
              Select a valid From and To date range. From must be on or before To.
            </Alert>
          ) : null}

          {anyFlyerReportFetchError ? (
            <Alert severity="error">
              {anyFlyerReportFetchError.message || "Something went wrong while loading flyer report data."}
            </Alert>
          ) : null}

          {flyerReportQueriesReady ? (
            <Stack spacing={2}>
              {!anyFlyerReportFetchError ? (
                <FlyerReportSummaryCards
                  flyerSummaryStats={flyerSummaryStats}
                  isLoading={isSummaryStatsLoading}
                />
              ) : null}

              <FlyerReportCharts
                selectedRetailer={selectedRetailer}
                selectedProvince={selectedProvince}
                retailerStackedBarChart={retailerStackedBarChart}
                promotionalFrequencyLineChart={promotionalFrequencyLineChart}
                provinceOfferCountChart={provinceOfferCountChart}
                isRetailerChartLoading={isRetailerChartLoading}
                isFrequencyChartLoading={isFrequencyChartLoading}
                isProvinceChartLoading={isProvinceChartLoading}
                retailerChartError={retailerChartError}
                frequencyChartError={frequencyChartError}
                provinceChartError={provinceChartError}
              />

              <FlyerGallery
                displayItems={galleryDisplayItems}
                serverPagination={galleryServerPagination}
                isLoading={isGalleryInitialLoading}
                isRefreshing={isGalleryRefreshing}
                error={galleryFetchError}
                onCardClick={handleGalleryCardClick}
              />
            </Stack>
          ) : null}

          <FlyerGalleryDetailDialog
            open={flyerReportQueriesReady && Boolean(selectedGalleryDisplayItem)}
            displayItem={selectedGalleryDisplayItem}
            onClose={handleCloseFlyerGalleryDetail}
          />
        </Stack>
      </Box>
    </Stack>
  );
}
